import { useNavigate } from "react-router-dom";
import { Shield, Clock, CheckCircle, ArrowRight, Lock } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1771773490670-7376a45c0e96?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const VALUE_PROPS = [
  {
    icon: Shield,
    title: "DISP Membership Tier",
    desc: "Are you even eligible for DISP?\nInstantly estimate your likely membership tier based on your security profile.",
    boldPrefix: "Are you even eligible for DISP?",
  },
  {
    icon: CheckCircle,
    title: "Readiness Score",
    desc: "How far are you from ML2?\nGet a scored breakdown of your current security maturity against key DISP expectations.",
    boldPrefix: "How far are you from ML2?",
  },
  {
    icon: Lock,
    title: "FOCI Risk Indicator",
    desc: "Could FOCI block your application?\nIdentify Foreign Ownership, Control or Influence risks early—before they delay approval.",
    boldPrefix: "Could FOCI block your application?",
  },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a1f33] text-[#f1f5f9] font-['Inter',sans-serif]">
      {/* Hero */}
      <div className="relative min-h-[88vh] flex items-center overflow-hidden">
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0a1f33]/85" />

        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(to right,#1e3a5f 1px,transparent 1px),linear-gradient(to bottom,#1e3a5f 1px,transparent 1px)",
            backgroundSize: "4rem 4rem",
          }}
        />

        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 lg:px-12 py-24">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 border border-[#c9a84c]/40 rounded-sm px-3 py-1.5 mb-8"
            data-testid="hero-badge">
            <span className="w-2 h-2 rounded-full bg-[#c9a84c] animate-pulse" />
            <span className="text-[#c9a84c] text-xs font-['Manrope',sans-serif] font-semibold tracking-widest uppercase">
              Australian Defence Industry
            </span>
          </div>

          <h1
            className="font-['Manrope',sans-serif] font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-tight tracking-tight mb-6"
            data-testid="hero-title"
          >
            <span className="inline-flex items-center gap-3">
              <img
                src="/logo.png"
                alt="DefencIQ logo"
                style={{ height: "0.85em", width: "auto" }}
              />
              <span>
                <span style={{ fontWeight: 300 }}>Defenc</span><span style={{ fontWeight: 800 }}>IQ</span><span style={{ fontSize: "0.5em", verticalAlign: "super", fontWeight: 300 }}>™</span>
              </span>
            </span>
            <span className="block text-[#c9a84c]">DISP Quick Start</span>
            <span className="block text-[#c9a84c] text-lg sm:text-xl lg:text-2xl font-medium mt-2 tracking-wide">DISP + Essential Eight ML2 Readiness Check</span>
          </h1>

          <p className="text-[#94a3b8] text-lg sm:text-xl max-w-2xl mb-8 leading-relaxed">
            How close is your organisation to meeting DISP + Essential Eight ML2 requirements?
          </p>

          <div className="flex flex-wrap items-center gap-4 my-8">
            <button
              data-testid="start-assessment-btn"
              onClick={() => navigate("/assessment")}
              className="inline-flex items-center gap-2 bg-[#c9a84c] text-[#0a1f33] font-['Manrope',sans-serif] font-bold text-sm uppercase tracking-widest px-8 py-4 rounded-sm shadow-[0_0_24px_rgba(201,168,76,0.35)] hover:bg-[#d4b460] transition-all duration-200 hover:-translate-y-0.5"
            >
              Check My DISP Readiness
              <ArrowRight size={16} />
            </button>

            <div className="flex items-center gap-2 text-[#94a3b8] text-sm">
              <span>2 minutes &bull; Instant results</span>
            </div>
          </div>
          <div className="mt-8 max-w-2xl">
            <p className="text-[#f59e0b] font-bold mb-2" style={{ fontSize: "1.5rem" }}>Why this matters</p>
            <p className="text-[#94a3b8] text-sm leading-relaxed mb-2">
              From November 2025, organisations seeking DISP membership are expected to align with Essential Eight ML2.
            </p>
            <p className="text-[#f59e0b] text-sm leading-relaxed">
              ⚠️ Most organisations we assess are <span className="font-bold">30–50% short of ML2</span>—often without realising it
            </p>
          </div>
        </div>
      </div>

      {/* Value props */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20">
        <h2
          className="font-['Manrope',sans-serif] font-bold text-2xl text-[#f1f5f9] mb-12 text-center"
          data-testid="value-props-heading"
        >
          What this assessment covers
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {VALUE_PROPS.map(({ icon: Icon, title, desc, boldPrefix }, i) => (
            <div
              key={i}
              className="bg-[#112d4e] border border-[#1e3a5f] rounded-sm p-6 hover:border-[#c9a84c]/40 transition-colors duration-300"
              data-testid={`value-prop-${i}`}
            >
              <div className="w-10 h-10 rounded-sm bg-[#c9a84c]/15 flex items-center justify-center mb-4">
                <Icon size={20} className="text-[#c9a84c]" />
              </div>
              <h3 className="font-['Manrope',sans-serif] font-semibold text-[#f1f5f9] mb-2" style={{ fontSize: "1.25rem" }}>
                {title}
              </h3>
              <p className="text-[#94a3b8] text-sm leading-relaxed">
                {boldPrefix && <><span className="font-bold text-[#f1f5f9]">{boldPrefix}</span><br /></>}
                {boldPrefix ? desc.replace(boldPrefix + "\n", "") : desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Banner */}
      <div className="border-t border-[#1e3a5f] bg-[#112d4e]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-['Manrope',sans-serif] font-bold text-lg text-[#f1f5f9]">
              Ready to assess your DISP readiness?
            </p>
            <p className="text-[#94a3b8] text-sm mt-1">
              13 questions across 3 sections. Free. No account required.
            </p>
          </div>
          <button
            data-testid="cta-banner-btn"
            onClick={() => navigate("/assessment")}
            className="whitespace-nowrap inline-flex items-center gap-2 bg-[#c9a84c] text-[#0a1f33] font-['Manrope',sans-serif] font-bold text-sm uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-[#d4b460] transition-all duration-200"
          >
            Check My DISP Readiness
            <ArrowRight size={15} />
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#1e3a5f] py-6 text-center text-[#334155] text-xs">
        DefencIQ™ DISP Quick Start · For Australian SMEs · Not official DISP advice
      </footer>
    </div>
  );
}
